import { useDispatch } from "react-redux";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import AddModel from "../../../components/Add/AddModel";
import AddInputs from "../../../components/Add/AddInputs";
import { useNavigate, useParams } from "react-router-dom";
import { useLayoutEffect, useState } from "react";
import AddBottomButton from "../../../components/Add/AddBottomButton";
import validateMandatoryFields from "../../../components/commonfunctions/validateMandatoryFields";
import ObjectBuilder from "../../../components/commonfunctions/ObjectBuilder";
import useApi from "../../../services/useApi";
import { showToast } from "../../../components/toastMessage/toastSlice";
import { getChangedFields } from "../../../components/commonfunctions/getChangedKeys";
import deepEqual from "../../../components/commonfunctions/deepEqual";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";

interface stateTypes {
    "name": string;
    "admin_cashier_name": string;
    "country_code": string;
    "cashier_phone_number": string;
    "country": string;
    "province": string;
    "region": string;
    "district": string;
    "city_town": string;
    "office_no": string;
    "market_block_no": string;
    "floor_no": string;
    "postal_code": string;
}
interface props {
    edit?: boolean;
}
interface error { [key: string]: string }
export default function AddRegionalOffice({ edit=false }: props) {
    const initialState = {
        name: "",
        admin_cashier_name: "",
        country_code: "",
        cashier_phone_number: "",
        country: "",
        province: "",
        region: "",
        district: "",
        city_town: "",
        office_no: "",
        market_block_no: "",
        floor_no: "",
        postal_code: ""
    }
    //states used in the feature
    const [state, setState] = useState<stateTypes>(initialState)
    const [temporaryState, setTemporaryState] = useState<stateTypes>(initialState)
    const [errors, setErrors] = useState<error>(initialState)
    const [loading, setLoading] = useState(false)
    const [editLoading, setEditLoading] = useState(false)
    const { PostAPI, getAPI, PatchAPI } = useApi()
    const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { id } = useParams();

    // Input configuration with conditional logic based on the selected country
    const inputs = {
        "": {
            "Regional Office Name": {
                required: true,
                disabled: false || edit,
                id: "name",
                type: "input",
                maxLength: 60,
                tabIndex: 0,
            },
            "Admin Cashier Name": {
                required: true,
                disabled: false,
                id: "admin_cashier_name",
                type: "input",
                maxLength: 60,
                tabIndex: 1,
            },
            "Contact Details of Cashier": {
                required: true,
                disabled: false,
                id: "cashier_phone_number",
                type: "phone_number_no_verify",
                tabIndex: 3,
            },
        },
        "Regional Office Address": {
            Country: {
                required: false,
                disabled: false,
                id: "country",
                type: "country_type", // Adjust if needed
                tabIndex: 9,
            },
            ...(state?.country !== '' && {
                "Office No.": {
                    required: false,
                    disabled: false,
                    id: "office_no",
                    type: "input",
                    maxLength: 60,
                    tabIndex: 7,
                }
            }),
            ...(state?.country !== '' && {
                "Market/Block No.": {
                    required: false,
                    disabled: false,
                    id: "market_block_no",
                    type: "input",
                    tabIndex: 5,
                    maxLength: 60,
                }
            }),
            ...(state?.country !== '' && {
                "Floor No.": {
                    required: false,
                    disabled: false,
                    id: "floor_no",
                    type: "input",
                    maxLength: 60,
                    tabIndex: 6,
                }
            }),
            ...(state?.country !== '' && {
                "Postal Code": {
                    required: true,
                    disabled: state?.country === "Afghanistan", // Postal code is not mandatory for Afghanistan
                    id: "postal_code",
                    type: "input",
                    maxLength: 60,
                    tabIndex: 7,
                    placeholder: `${state?.country === "Afghanistan" ? 'Auto-filled based on district' : ''}`,
                }
            }),
        },
    };

    // Handle input changes
    const handleInputChange = (id: string, value: string) => {
        setErrors((prev) => ({
            ...prev,
            [id]: ''
        }))
        setState((prev) => ({
            ...prev,
            [id]: value
        }))
    }

    // Submit handler
    const handleSubmit = async () => {
        // Define mandatory fields based on the selected country
        let mandatoryFields = []
        if (state.country === '') {
            mandatoryFields = ['name', 'admin_cashier_name', 'cashier_phone_number', 'country'];
        } else if (state.country !== 'Afghanistan') {
            mandatoryFields = ['name', 'admin_cashier_name', 'cashier_phone_number', 'country', 'province', 'region', 'postal_code'];
        } else {
            if (state?.province == '') {
                mandatoryFields = ['name', 'admin_cashier_name', 'cashier_phone_number', 'country', 'province'];
            } else {
                mandatoryFields = ['name', 'admin_cashier_name', 'cashier_phone_number', 'country', 'province', 'district'];
            }
        }

        // Validate mandatory fields
        let errorObj = validateMandatoryFields(state, mandatoryFields)
        if (Object.keys(errorObj).length > 0) {
            setErrors(errorObj);
            return;
        }

        // Prepare payload for API request
        let payload: { [key: string]: any } = {};
        if (edit) {
            payload = getChangedFields(temporaryState, state);
        } else {
            payload = ObjectBuilder(initialState, state)
        }
        if(payload.cashier_phone_number &&  payload.cashier_phone_number !==''){
            payload.cashier_phone_number = `${payload.cashier_phone_number.replace(state?.country_code,'')}`;
            payload.country_code = `+${state.country_code}`
        }

        // Call API to save or update regional office details
        setLoading(true)
        let response;
        if (edit) {
            response = await PatchAPI(`regional-offices/${id}`, payload);
        } else {
            response = await PostAPI(`regional-offices/`, payload)
        }
        if (!response?.error) {
            dispatch(
                showToast({
                    type: "success",
                    message: edit ? 'Regional office details updated successfully' : response?.data?.message,
                })
            );
            navigate(pathname ? pathname : '/other-resources/regional-offices')
        } else {
            console.log(response, 'API ERROR')
            dispatch(
                showToast({
                    type: "error",
                    message: response?.data?.response?.data?.message,
                })
            );
        }
        setLoading(false);
    }

    // Fetch regional office details for editing
    const getRegionalOfficeDetails = async () => {
        setEditLoading(true)
        let response = await getAPI(`regional-offices/${id}`);
        if (!response?.error && response?.data?.data) {
            let obj = initialState;
            obj = ObjectBuilder(initialState, response.data.data);
            obj.cashier_phone_number = obj.country_code.split('+')[1] + obj.cashier_phone_number;
            obj.country_code = obj.country_code.split('+')[1] || '';
            setState(obj);
            setTemporaryState(obj)
        } else {
            console.log(response, "API ERROR OCCURED");
            navigate('/not-found', { state: { route: '/other-resources/regional-offices' } })
        }
        setEditLoading(false)
    }

    // Fetch details if in edit mode
    useLayoutEffect(() => {
        edit && getRegionalOfficeDetails();
    }, [])

    return (
        <div className="">
            <Breadcrumbs
                navPath={[
                    "Other Resources",
                    "Regional Offices",
                    "List of Regional Office",
                    `${edit ? 'Edit' : 'Add'} Regional Office`,
                ]}
                heading={`${edit ? "Edit" : "Add"} Regional Office`}
                links={pathname ? [pathname, pathname] : [
                    '/other-resources/regional-offices',
                    '/other-resources/regional-offices',
                    '/other-resources/regional-offices'
                ]}
            />
            <form onSubmit={(e) => {
                loading ? "" : e.preventDefault();
                handleSubmit();
            }}>
                <AddModel noScroll={false}>
                    <AddInputs
                        inputs={inputs}
                        totalInputCount={7}
                        isDisabledField={id ? ['email_address'] : []}
                        inputValues={state}
                        onInputChange={handleInputChange}
                        mapping={true}
                        errors={errors}
                        editLoading={editLoading}
                    />
                </AddModel>
                <AddBottomButton
                    buttonWidth={edit ? 'w-[278px]' : 'w-[250px]'}
                    buttonName={`${!edit ? 'Add' : 'Update'} Regional Office`}
                    buttonClass={`${edit ? 'w-[278px]' : 'w-[250px]'} whitespace-nowrap`}
                    onClick={handleSubmit}
                    disabled={loading || (edit && deepEqual(state, temporaryState))}
                    loading={loading}
                    testID={edit ? 'edit_regional_office' : "add_regional_office"}
                />
            </form>
        </div>
    );
}